import { isExperimentViewportSize } from '@client/adManager/AdUnit/utils/smallMobileScreensExperiment.js';
import {
  debug as _debug,
  Rgb,
  type GamKeyValue,
  type PlacementId
} from '@schibsted-nmp/advertising-shared';

import { $amplitudeExperimentsAtom } from '../../atoms/amplitude.js';
import { setGamTargetingAtom } from '../../atoms/gamTargeting.js';
import { addLifecycleEvent } from '../../atoms/metrics.js';

const debugLabel = 'Amplitude';
const debug = _debug.withLabel(debugLabel, Rgb.blueBright, Rgb.blueLight);

type ExperimentTargeting = {
  key: string;
  value: string;
};

// This will be accessed when setting up individual slots
const placementExperimentTargeting = new Map<
  PlacementId,
  ExperimentTargeting
>();

/**
 * Extracts the active Amplitude experiment and sends it to Google Ad Manager as a targeting key-value pair.
 * If the experiment payload contains a "placements" array, the targeting will only be applied to those placements
 * that have 'enabled' set to true.
 * Otherwise, it will be applied globally to all placements.
 */
export function sendAmplitudeExperimentToGam(): void {
  try {
    const { experiments, payloads } = $amplitudeExperimentsAtom.get();

    if (Object.keys(experiments).length === 0) {
      debug
        .message('No active experiments found, skipping', {
          experiments,
          payloads
        })
        .verbose();

      return;
    }

    placementExperimentTargeting.clear();

    const globalExperiments = new Array<ExperimentTargeting>();

    for (const [experimentName, value] of Object.entries(experiments)) {
      const debugExperiment = debug.withSublabel(debugLabel, experimentName);

      if (!value) {
        debugExperiment
          .message('Skipping experiment with empty value')
          .verbose();

        continue;
      }

      if (
        experimentName === 'advt-small-mobile-screen-tori' &&
        !isExperimentViewportSize()
      ) {
        continue;
      }

      const experimentPayload = payloads[experimentName];

      debugExperiment.message`Processing experiment with value ${value}`({
        experimentPayload
      }).verbose();

      const targetPlacements = experimentPayload?.placements?.filter(
        (placement) => placement.enabled
      );

      if (targetPlacements?.length) {
        for (const targetPlacement of targetPlacements) {
          debugExperiment
            .withPlacementLabel(targetPlacement.placementId)
            .message('Storing experiment for placement')
            .verbose();

          placementExperimentTargeting.set(targetPlacement.placementId, {
            key: 'experiment',
            value: getFormattedValue(value, experimentName)
          });
        }
      } else {
        debugExperiment
          .message('Adding experiment to global targeting')
          .verbose();

        globalExperiments.push({
          key: 'experiment',
          value: getFormattedValue(value, experimentName)
        });
      }

      addLifecycleEvent(`Amplitude experiment ${experimentName} prepared`);
    }

    // Apply global experiments to all placements
    if (globalExperiments.length > 0) {
      const gamTargeting = globalExperiments.map<GamKeyValue>(
        ({ key, value }) => ({ key, value: [value] })
      );

      debug
        .message('Sending global experiments to GAM targeting', gamTargeting)
        .verbose();

      setGamTargetingAtom(gamTargeting);
      addLifecycleEvent('Global Amplitude experiments sent to GAM targeting');
    }
  } catch (error) {
    debug.message('Failed sending experiment to GAM!', error).error();

    const message = error instanceof Error ? error.message : 'unknown';

    addLifecycleEvent(`Error sending Amplitude experiment to GAM: ${message}`);
  }
}

export function getExperimentTargetingForPlacement(
  placementId: PlacementId
): ExperimentTargeting | undefined {
  return placementExperimentTargeting.get(placementId);
}

/**
 * Sets up a subscription to the Amplitude experiments atom to update GAM targeting
 * whenever experiments change.
 */
export function setupAmplitudeExperimentSubscription(): void {
  try {
    $amplitudeExperimentsAtom.subscribe((value) => {
      debug
        .message('Experiments changed - updating GAM targeting', value)
        .info((log) => log());

      sendAmplitudeExperimentToGam();
    });

    debug.message('Completed experiment subscription setup').success();
  } catch (error) {
    debug.message('Failed setting up experiment subscription', error).error();

    const message = error instanceof Error ? error.message : 'unknown';

    addLifecycleEvent(
      `Error setting up Amplitude experiment subscription: ${message}`
    );
  }
}

function getFormattedValue(value: string, experimentName: string): string {
  const formattedExperimentName = experimentName.replace(/-/g, '_');
  return `${formattedExperimentName}_${value}`;
}
